import { useCallback, useEffect, useMemo, useState } from 'react';
import type { CSSProperties, FC, KeyboardEvent } from 'react';
import { Placeholder } from '@tiptap/extensions';
import { EditorContent, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { Button, Space } from 'antd';
import { TbArrowUp, TbAt } from 'react-icons/tb';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import type { CommentComposerValue, ResourceActivityResourceType } from '@/types';

import { createCommentMentionNode } from './CommentMentionNode';
import { serializeCommentEditor } from './serialization';

import './index.css';

interface CommentComposerProps {
    appearance?: 'default' | 'dropdown';
    autoFocus?: boolean;
    disabled?: boolean;
    maxHeight?: string | number;
    minHeight?: string | number;
    onSubmit: (value: CommentComposerValue) => Promise<boolean | void> | boolean | void;
    placeholder?: string;
    resourceId: string;
    resourceType: ResourceActivityResourceType;
    showSubmitButton?: boolean;
    submitLabel?: string;
    submitting?: boolean;
}

const CommentComposer: FC<CommentComposerProps> = ({
    appearance = 'default',
    autoFocus,
    disabled,
    maxHeight = '8rem',
    minHeight = '4.25rem',
    onSubmit,
    placeholder = 'Add Comment...',
    resourceId,
    resourceType,
    showSubmitButton = true,
    submitLabel,
    submitting,
}) => {
    const { token } = useAntDesignTheme();
    const [value, setValue] = useState<CommentComposerValue>({ mentions: [], message: '' });
    const [internalSubmitting, setInternalSubmitting] = useState(false);
    const mentionExtension = useMemo(
        () => createCommentMentionNode({ resourceId, resourceType }),
        [resourceId, resourceType],
    );
    const editor = useEditor({
        content: '',
        immediatelyRender: false,
        editorProps: {
            attributes: {
                'aria-label': placeholder,
                role: 'textbox',
            },
        },
        extensions: [
            StarterKit.configure({
                blockquote: false,
                bulletList: false,
                codeBlock: false,
                heading: false,
                horizontalRule: false,
                orderedList: false,
            }),
            Placeholder.configure({ placeholder }),
            mentionExtension,
        ],
        onUpdate: ({ editor: nextEditor }) => setValue(serializeCommentEditor(nextEditor)),
    });

    useEffect(() => {
        editor?.setEditable(!disabled);
    }, [disabled, editor]);

    useEffect(() => {
        if (!autoFocus || !editor) {
            return;
        }

        const timeout = window.setTimeout(() => editor.commands.focus('end'), 0);

        return () => window.clearTimeout(timeout);
    }, [autoFocus, editor]);

    const submit = useCallback(async () => {
        if (!editor || !value.message || disabled || submitting || internalSubmitting) {
            return;
        }

        try {
            setInternalSubmitting(true);
            const submitted = await onSubmit(value);

            if (submitted === false) {
                return;
            }

            editor.commands.clearContent(true);
            setValue({ mentions: [], message: '' });
        } finally {
            setInternalSubmitting(false);
        }
    }, [disabled, editor, internalSubmitting, onSubmit, submitting, value]);

    const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
        if (event.key === 'Enter' && (event.metaKey || event.ctrlKey)) {
            event.preventDefault();
            void submit();
        }
    };
    const loading = Boolean(submitting || internalSubmitting);
    const insertMentionTrigger = () => {
        if (!editor || disabled) {
            return;
        }

        editor.chain().focus('end').insertContent('@').run();
    };
    const editorContent = (
        <div
            className='comment-composer__editor'
            style={
                {
                    '--comment-placeholder-color': token.colorTextPlaceholder,
                    background: token.colorBgContainer,
                    border: `1px solid ${token.colorBorder}`,
                    borderRadius: token.borderRadius,
                    flex: 1,
                    maxHeight,
                    minHeight,
                    minWidth: 0,
                    opacity: disabled ? 0.65 : 1,
                    overflow: 'hidden',
                } as CSSProperties
            }
        >
            <EditorContent editor={editor} />
        </div>
    );
    const composerActions = showSubmitButton ? (
        <Space.Compact
            size='small'
            style={{ borderRadius: token.borderRadius, display: 'inline-flex', overflow: 'hidden' }}
        >
            <Button
                aria-label='Mention a person, team, or workspace resource'
                disabled={disabled}
                icon={<TbAt size='1rem' style={{ color: token.colorTextTertiary }} />}
                style={{
                    alignItems: 'center',
                    display: 'inline-flex',
                    height: '2rem',
                    justifyContent: 'center',
                    minWidth: '2rem',
                    width: '2rem',
                }}
                onClick={insertMentionTrigger}
                onMouseDown={(event) => event.preventDefault()}
            />
            <Button
                aria-label={submitLabel ?? 'Add comment'}
                disabled={disabled || !value.message}
                icon={<TbArrowUp size='1rem' />}
                loading={loading}
                style={{
                    alignItems: 'center',
                    display: 'inline-flex',
                    height: '2rem',
                    justifyContent: 'center',
                    minWidth: '2rem',
                    width: '2rem',
                }}
                type='primary'
                onClick={() => void submit()}
            />
        </Space.Compact>
    ) : null;

    if (appearance === 'dropdown') {
        return (
            <div
                onKeyDown={handleKeyDown}
                style={{
                    boxSizing: 'border-box',
                    minWidth: 0,
                    width: '100%',
                }}
            >
                {editorContent}
                {showSubmitButton && (
                    <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '0.5rem' }}>
                        {composerActions}
                    </div>
                )}
            </div>
        );
    }

    return (
        <div
            onKeyDown={handleKeyDown}
            style={{ alignItems: 'flex-end', display: 'flex', gap: '0.5rem', width: '100%' }}
        >
            {editorContent}
            {composerActions}
        </div>
    );
};

export default CommentComposer;
